import { Controller } from "@hotwired/stimulus"

// Card #4461 (Playbooks D, ADR-0157 decision 3), widened by card #4465
// (umbrella #4197) to the Mission Control "play from the builder" surface.
// An Article-to-Article reference embedded in a Playbook entry's rich text
// body must open a read-only preview modal and never navigate away,
// regardless of which surface is playing the Playbook. The embedded
// reference markup (app/views/articles/_article.html.erb) is shared with
// the Help Centre, so this controller intercepts clicks on it rather than
// changing that partial's link behaviour globally -- the only addition
// there is a `data-article-id` attribute, which is inert everywhere else.
//
// Originally field_playbook_article_controller.js (Field App only). Renamed
// and generalised for card #4465 rather than duplicated: every byte of this
// controller's logic is surface-agnostic already -- the only thing that
// ever differed between the Field App and the Mission Control player is
// which route helper built the preview URL template, and that was already
// parameterised as a Stimulus value. Both app/views/field/playbook_entries
// and app/views/playbooks/play_entries supply their own
// previewUrlTemplateValue and register this same controller.
//
// The frame is loaded via src=, same pattern as response_modal_controller.js
// and mission_task_modal_controller.js: Rails generates the URL (a template
// with the entry's own id substituted client-side), Turbo renders the
// partial from the server. No HTML is built here.
//
// Targets:
//   content -- wraps the rendered article body (click delegation scope)
//   dialog  -- the <dialog> element
//   frame   -- the turbo-frame the preview partial loads into
//
// Values:
//   previewUrlTemplate -- a *_preview_article_*_path with the literal token
//                         "__ARTICLE_ID__" where the referenced Article's
//                         id belongs
export default class extends Controller {
  static targets = ["content", "dialog", "frame"]
  static values = { previewUrlTemplate: String }

  // Delegated to the content wrapper (data-action lives on the controller's
  // root element, Stimulus scope rule) so references rendered by any future
  // Turbo update are still caught without re-binding.
  interceptReference(event) {
    const link = event.target.closest("[data-article-id]")
    if (!link || !this.hasContentTarget || !this.contentTarget.contains(link)) return

    event.preventDefault()
    const articleId = link.dataset.articleId
    if (!articleId || !this.hasPreviewUrlTemplateValue) return

    const url = this.previewUrlTemplateValue.replace("__ARTICLE_ID__", articleId)
    this.frameTarget.setAttribute("src", url)
    this.dialogTarget.showModal()
  }

  // Backdrop click closes, same as the native ESC handling <dialog> already
  // gives us for free.
  closeOnBackdrop(event) {
    if (event.target === this.dialogTarget) this.close()
  }

  close() {
    this.dialogTarget.close()
  }
}
